/**
 * `/agents/triage` — Phase 3.H triage board UI (#46036).
 *
 * Wires the existing #45530 backend (`excellent-mcp triage list`) into a
 * DataTable. Does not re-rank or redesign bands.
 */

import { createRoute } from "@tanstack/react-router";
import { RouterLink as Link, useSearch } from "@excellent/ui/router";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  IntentBadge,
  PageHeader,
  StatStrip,
  formatRelativeTime,
  type DataTableColumn,
  type StatStripItem,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import {
  triageBoard,
  type TriageBand,
  type TriageBoardRowDto,
  type TriageBoardViewDto,
} from "@excellent/ui/api/contracts/triage-board";
import { rootRoute } from "./__root";

const NUM = "tabular-nums";
const MUTED = "type-subtext text-muted-foreground";

function bandIntent(band: TriageBand): "danger" | "warning" | "info" | "neutral" | "success" {
  if (band === "P0" || band === "HUMAN") return "danger";
  if (band === "P1") return "warning";
  if (band === "P2") return "info";
  if (band === "P3") return "neutral";
  return "success";
}

function AgentsTriagePage() {
  const { data, error, isLoading } = useApi(triageBoard.list, { query: { limit: 100 } });
  const searchParams = useSearch();
  const q = (searchParams.get("q") ?? "").trim().toLowerCase();

  const oracleError = error
    ? error instanceof Error
      ? error.message
      : String(error)
    : data && !data.ok
      ? data.error
      : null;

  return (
    <div className="px-6 py-8 space-y-6">
      <PageHeader
        title="Triage"
        description="The ranked board the agent queue already pulls from — #45530 rank, not a second score."
        search="Search triage"
      />

      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Reading the triage board…</p>
      ) : oracleError !== null || !data?.ok ? (
        <Card>
          <CardHeader>
            <CardTitle>Triage board unavailable</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="type-body text-muted-foreground">
              Could not read <code>excellent-mcp triage list</code>. Rebuild the
              MCP CLI if the dist is stale.
            </p>
            <p className={`${MUTED} mt-2 break-words`}>{oracleError}</p>
          </CardContent>
        </Card>
      ) : (
        <TriageBody status={data.status} query={q} />
      )}
    </div>
  );
}

function TriageBody({ status, query }: { status: TriageBoardViewDto; query: string }) {
  const rows = query
    ? status.rows.filter(
        (r) =>
          String(r.id).includes(query) ||
          r.title.toLowerCase().includes(query) ||
          r.band.toLowerCase().includes(query) ||
          r.status.toLowerCase().includes(query) ||
          r.reasons.some((x) => x.toLowerCase().includes(query)),
      )
    : status.rows;

  const byBand = new Map<string, number>();
  for (const r of status.rows) byBand.set(r.band, (byBand.get(r.band) ?? 0) + 1);
  const rescope = status.rows.filter((r) => r.rescope).length;

  const headline: StatStripItem[] = [
    {
      title: "Ranked",
      value: String(status.total),
      intent: "neutral",
      description: `showing ${status.rows.length} of ${status.matched} matched`,
    },
    {
      title: "P0",
      value: String(byBand.get("P0") ?? 0),
      intent: (byBand.get("P0") ?? 0) > 0 ? "danger" : "success",
      description: "structural ≥ 55",
    },
    {
      title: "HUMAN",
      value: String(byBand.get("HUMAN") ?? 0),
      intent: (byBand.get("HUMAN") ?? 0) > 0 ? "danger" : "neutral",
      description: "human-gated",
    },
    {
      title: "Rescope",
      value: String(rescope),
      intent: rescope > 0 ? "warning" : "neutral",
      description: "bounce ≥ 3 — do not re-dispatch",
    },
  ];

  const cols: DataTableColumn<TriageBoardRowDto>[] = [
    {
      key: "band",
      header: "Band",
      sort: (a, b) => a.band.localeCompare(b.band) || b.score - a.score,
      render: (r) => (
        <IntentBadge intent={bandIntent(r.band)} withDot>
          {r.band}
        </IntentBadge>
      ),
    },
    {
      key: "score",
      header: "Score",
      sort: (a, b) => a.score - b.score,
      defaultDesc: true,
      render: (r) => <span className={`${NUM} text-foreground`}>{r.score}</span>,
    },
    {
      key: "structural",
      header: "Struct",
      sort: (a, b) => a.structural - b.structural,
      defaultDesc: true,
      render: (r) => <span className={`${NUM} ${MUTED}`}>{r.structural}</span>,
    },
    {
      key: "id",
      header: "Task",
      sort: (a, b) => a.id - b.id,
      render: (r) => (
        <Link href={`/issues?task=${r.id}`} className={`${NUM} font-medium text-foreground hover:underline`}>
          #{r.id}
        </Link>
      ),
    },
    {
      key: "title",
      header: "Title",
      sort: (a, b) => a.title.localeCompare(b.title),
      render: (r) => (
        <span className="block max-w-[52ch] truncate text-foreground" title={r.title}>
          {r.title}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => <span className={MUTED}>{r.status}</span>,
    },
    {
      key: "rescope",
      header: "Flags",
      render: (r) =>
        r.rescope ? (
          <IntentBadge intent="warning">rescope</IntentBadge>
        ) : (
          <span className={MUTED}>—</span>
        ),
    },
    {
      key: "reasons",
      header: "Why",
      render: (r) => (
        <span className={`${MUTED} block max-w-[48ch] truncate`} title={r.reasons.join(" · ")}>
          {r.reasons[0] ?? "no factor fired"}
        </span>
      ),
    },
  ];

  return (
    <>
      <StatStrip items={headline} />
      <p className={MUTED}>
        Ranked {formatRelativeTime(status.generatedAt)} from the live vault via{" "}
        <code>excellent-mcp triage list</code>.
      </p>
      <DataTable
        title={`${rows.length} task${rows.length === 1 ? "" : "s"}`}
        subtitle="same order the agent queue pulls — highest rank first"
        rows={rows}
        columns={cols}
        rowKey={(r) => r.id}
        empty="No open tasks on the triage board."
      />
    </>
  );
}

export const agentsTriageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/triage",
  component: AgentsTriagePage,
});
